import { cn } from "lib/utils"
import { SafetySignal } from "./types"

type FlagsCardProps = {
  signals: SafetySignal[]
  filter: "new" | "in-progress" | "complete"
  onFilterChange: (filter: "new" | "in-progress" | "complete") => void
  onSelectSignal: (signal: SafetySignal) => void
  counts?: Partial<Record<"new" | "in-progress" | "complete", number>>
  className?: string
}

const formatTimestamp = (value?: string | null) => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const now = new Date()
  const elapsedMilliseconds = now.getTime() - date.getTime()

  if (elapsedMilliseconds >= 0) {
    const elapsedMinutes = Math.floor(elapsedMilliseconds / 60_000)
    if (elapsedMinutes < 60) {
      const minutes = Math.max(1, elapsedMinutes)
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`
    }

    const elapsedHours = Math.floor(elapsedMinutes / 60)
    if (elapsedHours < 24) {
      return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`
    }

    const elapsedDays = Math.floor(elapsedHours / 24)
    if (elapsedDays < 7) {
      return `${elapsedDays} day${elapsedDays === 1 ? "" : "s"} ago`
    }
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  })
}

const formatSource = (source?: string | null) => {
  if (!source) return ""
  if (source === "meeting_transcript") return "From a meeting transcript"
  if (source === "mentorship_report") return "Member report"
  return source
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

const priorityLevels: Record<SafetySignal["priority"], number> = {
  low: 1,
  medium: 2,
  high: 3,
}

export function FlagsCard({ signals, filter, onFilterChange, onSelectSignal, counts, className }: FlagsCardProps) {
  const emptyStateLabel = {
    new: "new",
    "in-progress": "in progress",
    complete: "complete",
  }[filter]

  return (
    <section className={cn("cohort-open-section min-w-0", className)} id="admin-safety-signals" aria-labelledby="admin-safety-heading">
      <p className="cohort-section-eyebrow lg:hidden">Safety</p>
      <div className="cohort-card-heading">
        <h2 id="admin-safety-heading" className="text-[21px] text-[var(--cohort-ink)] lg:text-xl">
          <span className="lg:hidden">Signals to review</span>
          <span className="hidden lg:inline">Safety signals</span>
        </h2>
        <span className="text-sm text-[var(--cohort-ink-muted)]">{counts?.new ?? (filter === "new" ? signals.length : 0)} new</span>
      </div>
      <div className="flex flex-wrap gap-2.5 py-3" role="group" aria-label="Filter safety signals">
        {(["new", "in-progress", "complete"] as const).map((value) => (
          <button key={value} type="button" aria-pressed={filter === value} onClick={() => onFilterChange(value)} className="cohort-choice px-3.5 text-sm">
            {value === "in-progress" ? "In progress" : value.charAt(0).toUpperCase() + value.slice(1)} {counts?.[value] ?? (filter === value ? signals.length : 0)}
          </button>
        ))}
      </div>
      {signals.length === 0 ? (
        <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">
          No {emptyStateLabel} signals.
        </div>
      ) : (
        <div className="cohort-bounded-list cohort-safety-signal-list w-full" role="region" aria-label="Safety signals list">
          {signals.map((signal) => {
            const priorityLabel = `${signal.priority.charAt(0).toUpperCase()}${signal.priority.slice(1)} priority`
            const sourceLabel = formatSource(signal.source)
            const statusLabel = signal.status === "in_progress" ? "in progress" : signal.status
            const priorityLevel = priorityLevels[signal.priority]

            return (
              <button
                type="button"
                key={signal.id}
                onClick={() => onSelectSignal(signal)}
                className="cohort-row-action cohort-safety-signal-row"
              >
                <span
                  className="cohort-priority-meter"
                  data-priority={signal.priority}
                  data-level={priorityLevel}
                  data-testid={`safety-priority-meter-${signal.id}`}
                  aria-hidden="true"
                >
                  {[0, 1, 2].map((segmentIndex) => (
                    <span
                      key={segmentIndex}
                      className={cn(
                        "cohort-priority-meter__segment",
                        segmentIndex >= 3 - priorityLevel && "cohort-priority-meter__segment--filled",
                      )}
                      aria-hidden="true"
                    />
                  ))}
                </span>
                <span className="min-w-0">
                  <span className="cohort-safety-signal-row__meta">
                    <span className="cohort-safety-signal-row__priority" data-priority={signal.priority}>
                      {priorityLabel}
                    </span>
                    <span className="cohort-safety-signal-row__separator" aria-hidden="true" />
                    <span className="text-[13px] text-[var(--cohort-ink-muted)]">
                      {signal.status === "new" ? "Unviewed · " : ""}{formatTimestamp(signal.created_at)}
                    </span>
                  </span>
                  <span className="cohort-safety-signal-row__name">{signal.user_name}</span>
                  <span className="cohort-safety-signal-row__summary">{signal.summary}</span>
                  <span className="cohort-safety-signal-row__source">
                    {sourceLabel ? `${sourceLabel} · ` : ""}status {statusLabel}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
